/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useQueryClient } from "@tanstack/react-query";
import { ROUTES } from "../constants/constants";
import { useCreateBooking } from "../lib/react-query/useCreateBooking";
import { invalidateClientTerms } from "../lib/react-query/invalidateClientTerms";
import { useStepManager } from "./useStepManager";
import { useAuth } from "./useAuth";
import { useBooking } from "./useBooking";
import { useTokenStorage } from "./useTokenStorage";
import { setRootFormError } from "../utils/api/apiErrors";

export const useBookingMutation = () => {
  const queryClient = useQueryClient();
  const { selectedServices, setInvoice, setBookingResponse } = useBooking();
  const { navigateToStep } = useStepManager();
  const { mutate, isPending } = useCreateBooking();
  const { fetchClientDetails, token, client } = useAuth();
  const { setToken } = useTokenStorage();

  const handleBookingMutation = (bookingObj, setError, options = {}) => {
    mutate(bookingObj, {
      onSuccess: async (response) => {
        if (response.token) {
          setToken({ token: response.token, rememberMe: false, isAnonymous: false });

          if (response.token !== token || !client) {
            await fetchClientDetails();
          }
        }

        invalidateClientTerms(queryClient, bookingObj?.terms);

        if (!!response.invoice) {
          setInvoice(response.invoice);
          navigateToStep(ROUTES.CHECKOUT);
        } else {
          const productsMap = {};
          selectedServices.forEach(service => {
            if (service.products?.length > 0) {
              productsMap[service.id] = service.products;
            }
          });
          setBookingResponse({ ...response, _productsMap: productsMap });
          navigateToStep(ROUTES.BOOKING_STATUS);
        }
      },
      onError: (error) => {
        handleError(error, setError);
        if (options.onError) options.onError(error);
      },
    });
  };

  const handleError = (error, setError) => {
    if (!setError) return;

    setRootFormError(setError, error);
  };

  return {
    handleBookingMutation,
    isPending,
  };
};
